import React, { useState } from 'react';
import { Map, ChevronDown, ChevronUp, ExternalLink, Copy, Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, prism } from 'react-syntax-highlighter/dist/esm/styles/prism';

const MapLayerCard = ({ layerId, name, sql, onViewMap }) => {
  const [showSQL, setShowSQL] = useState(false);
  const [copied, setCopied] = useState(false);
  const isDark = document.documentElement.classList.contains('dark');

  const handleCopySQL = async () => {
    if (sql) {
      await navigator.clipboard.writeText(sql);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="mt-3 group">
      <div className={cn(
        "relative overflow-hidden rounded-xl border transition-all duration-300",
        "bg-gradient-to-br from-emerald-50/50 via-white to-emerald-50/30",
        "dark:from-emerald-950/20 dark:via-gray-900 dark:to-emerald-950/10",
        "border-emerald-200/50 dark:border-emerald-800/30",
        "shadow-sm hover:shadow-md hover:border-emerald-300/50 dark:hover:border-emerald-700/50",
        "animate-in fade-in slide-in-from-bottom-4 duration-500"
      )}>
        {/* Decorative gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent pointer-events-none" />

        <div className="relative p-3">
          {/* Header */}
          <div className="flex items-start gap-3">
            {/* Map Icon */}
            <div className={cn(
              "flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center",
              "bg-gradient-to-br from-emerald-500 to-emerald-600",
              "shadow-lg shadow-emerald-500/20",
              "group-hover:scale-110 transition-transform duration-300"
            )}>
              <Map size={16} className="text-white" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-emerald-600 dark:text-emerald-400 font-medium mb-0.5">
                    Map layer created
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white truncate">
                    {name || 'Map Layer'}
                  </h3>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Show Code Button */}
                  {sql && (
                    <button
                      onClick={() => setShowSQL(!showSQL)}
                      className={cn(
                        "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors",
                        "text-emerald-700 dark:text-emerald-400",
                        "hover:text-emerald-800 dark:hover:text-emerald-300",
                        "hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                      )}
                    >
                      {showSQL ? (
                        <>
                          <ChevronUp size={14} />
                          <span>Hide code</span>
                        </>
                      ) : (
                        <>
                          <ChevronDown size={14} />
                          <span>Show code</span>
                        </>
                      )}
                    </button>
                  )}

                  {/* View Map Button */}
                  {onViewMap && (
                    <button
                      onClick={onViewMap}
                      className={cn(
                        "flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg",
                        "bg-emerald-600 hover:bg-emerald-700 text-white",
                        "font-semibold text-xs transition-all duration-200",
                        "shadow-md hover:shadow-lg hover:scale-105",
                        "active:scale-95"
                      )}
                    >
                      <ExternalLink size={14} />
                      <span>View Map</span>
                    </button>
                  )}
                </div>
              </div>

              {showSQL && (
                <div className="mt-2 relative">
                  <div className={cn(
                    "rounded-lg overflow-hidden border",
                    "border-gray-200 dark:border-gray-700",
                    "bg-gray-50 dark:bg-gray-950"
                  )}>
                    <div className="flex items-center justify-between px-3 py-1.5 bg-gray-100 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                        SQL Query
                      </span>
                      <button
                        onClick={handleCopySQL}
                        className={cn(
                          "flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-colors",
                          copied
                            ? "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30"
                            : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-800"
                        )}
                      >
                        {copied ? (
                          <>
                            <Check size={12} />
                            <span>Copied!</span>
                          </>
                        ) : (
                          <>
                            <Copy size={12} />
                            <span>Copy</span>
                          </>
                        )}
                      </button>
                    </div>
                    <div className="max-h-64 overflow-auto">
                      <SyntaxHighlighter
                        language="sql"
                        style={isDark ? vscDarkPlus : prism}
                        customStyle={{
                          margin: 0,
                          padding: '0.75rem',
                          fontSize: '11px',
                          lineHeight: '1.6',
                          background: 'transparent',
                        }}
                        codeTagProps={{
                          style: {
                            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                          }
                        }}
                      >
                        {sql}
                      </SyntaxHighlighter>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapLayerCard;
